import { Popper, Typography } from '@mui/material';
import { BarController, BarElement, CategoryScale, Chart as ChartJS, LinearScale } from 'chart.js';
import { Bar } from 'react-chartjs-2';

import { AXLE_TEAL } from '../../constants/colors';
import { NumVisitsByWeek, useQhpUtilization } from '../../fetching/api/qhpUtilizationMetrics';
import { AxleSuspense, SECTION_SKELETON_HEIGHT } from '../../fetching/suspense';
import { useStateWithDerivedBoolean } from '../../utils/booleanState';
import { startOfCurrentHour } from '../../utils/datetimes/misc';
import { ReturnComponent } from '../../utils/reactTypes';
import { QhpChip, QhpChipProps } from '../displayFields/chips';
import { Outline } from '../layout/Outline';
import { VerticalSpace, VerticalStack } from '../utils/linearLayouts';

ChartJS.register(CategoryScale, LinearScale, BarElement, BarController);

const NumVisitsByWeekForQhp = ({ numVisitsByWeek }: { numVisitsByWeek: NumVisitsByWeek }) => {
    return (
        <div style={{ textAlign: 'center' }}>
            <Typography variant={'h3'}># Visits Per Week</Typography>
            <VerticalSpace spacing={1} />
            <Bar
                data={{
                    labels: numVisitsByWeek.map(
                        (weekData) =>
                            `${weekData.start_date.month() + 1}/${weekData.start_date.date()} - ${
                                weekData.end_date.month() + 1
                            }/${weekData.end_date.date()}`,
                    ),
                    datasets: [
                        {
                            data: numVisitsByWeek.map((weekData) => weekData.num_visits),
                            backgroundColor: AXLE_TEAL,
                        },
                    ],
                }}
                options={{
                    scales: {
                        y: {
                            title: {
                                display: true,
                                text: 'Visits',
                            },
                            min: 0,
                            max: 40,
                        },
                    },
                }}
            />
        </div>
    );
};

// Show <QhpChip/> with a little pop-over that shows how busy the QHP is.
export const QhpUtilization = ({ qhpId }: { qhpId: string }) => {
    const today = startOfCurrentHour();
    const {
        future_caseload: { num_care_team_patients, num_future_visits },
        num_visits_by_week,
    } = useQhpUtilization({ qhpId: qhpId, startDatetime: today });
    return (
        <Outline
            padding={1.5}
            sx={{
                maxWidth: 'max-content',
            }}
        >
            <VerticalStack>
                <Typography variant={'body1'}>
                    Current Caseload:{' '}
                    <strong>
                        {num_care_team_patients}{' '}
                        {num_care_team_patients > 1 ? 'patients' : 'patient'}
                    </strong>
                </Typography>
                <Typography variant={'body1'}>
                    Total Assigned Visits: <strong>{num_future_visits}</strong>
                </Typography>
                <VerticalSpace spacing={1} />
                <NumVisitsByWeekForQhp numVisitsByWeek={num_visits_by_week} />
            </VerticalStack>
        </Outline>
    );
};

export const QhpChipWithUtilization = (
    props: QhpChipProps & {
        // Because the `qhp` prop in QhpChip is optional,
        //  but we want to make sure it's specified.
        qhp: NonNullable<QhpChipProps['qhp']>;
    },
): ReturnComponent => {
    const [anchorEl, setAnchorEl, isPopoverOpen, closePopover] =
        useStateWithDerivedBoolean<HTMLElement>(null);
    return (
        <>
            <QhpChip
                {...props}
                onMouseEnter={(event) => setAnchorEl(event.currentTarget)}
                onMouseLeave={closePopover}
            />
            <Popper open={isPopoverOpen} placement='right-end' anchorEl={anchorEl}>
                <AxleSuspense skeletonHeight={SECTION_SKELETON_HEIGHT}>
                    <QhpUtilization qhpId={props.qhp.uuid} />
                </AxleSuspense>
            </Popper>
        </>
    );
};
